// ─────────────────────────────────────────────────────────────────────────────
// Separar o executável da data.
//
// A IA que lê o atendimento devolve os dois grudados com frequência:
//
//   "2.55 - 15/07/2026"   "GuardianNF 2.55 (15/07/26)"   "2.55 em 15.07.2026"
//
// O formulário tem dois campos, e o `<input type="date">` só aceita
// `AAAA-MM-DD`. Sem separar, ou a data ficava pendurada no número da versão, ou
// o campo de data ficava vazio com a informação ali do lado.
//
// A busca pela data acontece MESMO quando o campo de data já veio preenchido:
// o que importa é tirar a data de dentro do texto do executável.
//
// Módulo puro — testado em scripts/teste-executavel.mjs.
// ─────────────────────────────────────────────────────────────────────────────

// dd/mm/aaaa · dd-mm-aa · dd.mm.aaaa — o ano de 2 dígitos vira 20xx.
const DATA_BR = /(\b\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})\b/;
// aaaa-mm-dd, que é o formato que o input já usa.
const DATA_ISO = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/;

const doisDigitos = (n) => String(n).padStart(2, '0');

/** Valida antes de aceitar: "45/13/2026" não é data, é número de versão. */
function ehDataPlausivel(ano, mes, dia) {
  return ano >= 1990 && ano <= 2100 && mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31;
}

/**
 * Limpa o que sobra depois de arrancar a data do meio do texto.
 * Sem isso ficam coisas como "2.55 -", "2.55 ()" ou "2.55 em".
 */
function limparSobras(texto) {
  return String(texto || '')
    .replace(/\(\s*\)/g, '')                 // parênteses que ficaram vazios
    .replace(/\s*\b(de|em)\b\s*$/i, '')      // "2.55 em" → "2.55"
    .replace(/[\s\-–—/,.]+$/g, '')           // pontuação solta no fim
    .replace(/^[\s\-–—/,.]+/g, '')           // e no começo
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Separa o texto em `{ executavel, data }`.
 *
 * @param {string} texto        o que a IA devolveu no campo executável
 * @param {string} dataAtual    data já preenchida, se houver (AAAA-MM-DD)
 * @returns {{ executavel: string, data: string }} data no formato do input
 */
export function separarExecutavelData(texto, dataAtual = '') {
  const bruto = String(texto || '').trim();
  if (!bruto) return { executavel: '', data: dataAtual || '' };

  let data = '';
  let resto = bruto;

  const iso = bruto.match(DATA_ISO);
  if (iso) {
    const [, a, m, d] = iso.map(Number);
    if (ehDataPlausivel(a, m, d)) {
      data = `${a}-${doisDigitos(m)}-${doisDigitos(d)}`;
      resto = bruto.replace(iso[0], ' ');
    }
  }

  if (!data) {
    const br = bruto.match(DATA_BR);
    if (br) {
      const dia = Number(br[1]);
      const mes = Number(br[2]);
      // Ano de dois dígitos: "26" é 2026. Nenhum executável do Guardian é de 1926.
      const ano = br[3].length === 2 ? 2000 + Number(br[3]) : Number(br[3]);
      if (ehDataPlausivel(ano, mes, dia)) {
        data = `${ano}-${doisDigitos(mes)}-${doisDigitos(dia)}`;
        resto = bruto.replace(br[0], ' ');
      }
    }
  }

  return {
    executavel: limparSobras(resto),
    // A data encontrada no texto vence a que já estava: ela veio junto do
    // executável que a IA leu do atendimento, então é a mais específica.
    data: data || dataAtual || '',
  };
}
